import { motion } from "framer-motion";
import { useReferral } from "@/contexts/ReferralContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Users, Gift, CheckCircle2, UserPlus, ExternalLink,
  CalendarDays, TrendingUp, Wallet,
} from "lucide-react";
import ReferralLinkCard from "@/components/referral/ReferralLinkCard";
import WalletCard from "@/components/referral/WalletCard";
import PlanRewardTiers from "@/components/referral/PlanRewardTiers";

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`p-5 rounded-lg bg-card border border-border shadow-card ${className}`}>{children}</div>
);

const statusConfig = {
  clicked: { label: "Link Clicked", color: "bg-muted text-muted-foreground", icon: ExternalLink },
  signed_up: { label: "Signed Up", color: "bg-accent/10 text-accent border-accent/20", icon: UserPlus },
  subscribed: { label: "Subscribed", color: "bg-success/10 text-success border-success/20", icon: CheckCircle2 },
};

const rewardStatusConfig = {
  pending: { label: "Pending", color: "bg-muted text-muted-foreground" },
  earned: { label: "Earned", color: "bg-accent/10 text-accent border-accent/20" },
  redeemed: { label: "Redeemed", color: "bg-success/10 text-success border-success/20" },
  expired: { label: "Expired", color: "bg-destructive/10 text-destructive border-destructive/20" },
};

export default function ReferralDashboardPage() {
  const { referralLink, referrals, rewards, stats, wallet, redeemReward, redeemWallet } = useReferral();

  const handleRedeem = (id: string) => {
    const ok = redeemReward(id);
    if (ok) toast.success("Reward redeemed successfully!");
    else toast.error("Could not redeem reward.");
  };

  const conversionRate = stats.totalReferrals > 0
    ? Math.round((stats.successfulSignups / stats.totalReferrals) * 100) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-display text-foreground">Referral Program</h1>
        <p className="text-sm text-muted-foreground">Invite others to AccouPro and earn wallet rewards</p>
      </div>

      <ReferralLinkCard referralLink={referralLink} />

      <PlanRewardTiers />

      {/* Wallet */}
      <WalletCard wallet={wallet} onRedeem={redeemWallet} />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Total Referrals", value: stats.totalReferrals, icon: Users, color: "text-accent" },
          { label: "Successful Signups", value: stats.successfulSignups, icon: UserPlus, color: "text-success" },
          { label: "Wallet Balance", value: `₹${wallet.balance}`, icon: Wallet, color: "text-warning" },
          { label: "Free Months", value: stats.totalMonthsFree, icon: CalendarDays, color: "text-accent" },
        ].map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Card>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{stat.label}</span>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
              <div className="text-2xl font-bold font-display text-foreground">{stat.value}</div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Conversion Rate */}
      <Card>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-foreground">Conversion Rate</span>
          <span className="text-sm font-bold text-accent">{conversionRate}%</span>
        </div>
        <Progress value={conversionRate} className="h-2" />
        <div className="flex justify-between mt-1.5">
          <span className="text-xs text-muted-foreground">{stats.successfulSignups} signups from {stats.totalReferrals} clicks</span>
          <span className="text-xs text-muted-foreground">{stats.activeSubscribers} converted to paid</span>
        </div>
      </Card>

      {/* Referrals + Rewards Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Users className="h-4 w-4 text-accent" /> Referrals ({referrals.length})
          </h3>
          <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
            {referrals.map((ref) => {
              const cfg = statusConfig[ref.status];
              const StatusIcon = cfg.icon;
              return (
                <div key={ref.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/50">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-sm font-bold text-accent">
                      {ref.refereeName.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{ref.refereeName}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {ref.refereeEmail}
                        {ref.planPurchased && <span className="ml-1 text-accent">• {ref.planPurchased}</span>}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-[10px] ${cfg.color} flex items-center gap-1`}>
                    <StatusIcon className="h-2.5 w-2.5" /> {cfg.label}
                  </Badge>
                </div>
              );
            })}
            {referrals.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">No referrals yet. Share your link!</p>
            )}
          </div>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Gift className="h-4 w-4 text-accent" /> Rewards ({rewards.length})
          </h3>
          <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
            {rewards.map((rw) => {
              const cfg = rewardStatusConfig[rw.status];
              return (
                <div key={rw.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/50">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate">{rw.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {rw.type === "cash_credit" ? `₹${rw.value} credit` : rw.type === "free_month" ? `${rw.value} free month(s)` : `${rw.value}% discount`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {rw.status === "earned" && (
                      <Button size="sm" variant="outline" onClick={() => handleRedeem(rw.id)} className="text-xs h-7 px-2">
                        Redeem
                      </Button>
                    )}
                    <Badge variant="outline" className={`text-[10px] ${cfg.color}`}>{cfg.label}</Badge>
                  </div>
                </div>
              );
            })}
            {rewards.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">No rewards yet. Start referring!</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
